import React, { useState, useEffect, Fragment, useCallback, useMemo } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { SystemConstant } from "const";
import { useBranchRequestUpdate, useDepartmentDetails, useUnitDetails, useUnitList } from "hooks";
import { format } from "date-fns";
import SearchableSelect from "components/common/SearchableSelect";

const FormUpdateBranchRequest = ({ user, isOpenFormAccept, onCloseForm, refetch, onOpenConfirm }) => {
  const { updateBranchRequest } = useBranchRequestUpdate();

  const { totalUnitList } = useUnitList();
  const { positions: positionsByUnit, departments, handleGetUnitDetails } = useUnitDetails();
  const { positions: positionsByDepartment, handleGetDepartmentDetails } = useDepartmentDetails();
  const [positions, setPositions] = useState([]);

  const [updateBirthday, setUpdateBirthday] = useState(null);
  const [updateUnit, setUpdateUnit] = useState("");
  const [updateDepartment, setUpdateDepartment] = useState("");
  const [updatePosition, setUpdatePosition] = useState("");

  // Sort lists alphabetically
  const sortedUnitList = useMemo(() =>
    [...totalUnitList].sort((a, b) => (a.unitName || "").localeCompare(b.unitName || "")),
    [totalUnitList]
  );
  const sortedDepartments = useMemo(() =>
    [...departments].sort((a, b) => (a.departmentName || "").localeCompare(b.departmentName || "")),
    [departments]
  );
  const sortedPositions = useMemo(() =>
    [...positions].sort((a, b) => (a.positionName || "").localeCompare(b.positionName || "")),
    [positions]
  );

  const handleChangeUnit = value => {
    setUpdateUnit(value);
    handleGetUnitDetails(value);
    setUpdateDepartment("");
    setUpdatePosition("");
  };

  const handleChangeDepartment = value => {
    setUpdateDepartment(value);
    handleGetDepartmentDetails(value);
    setUpdatePosition("");
  };

  const handleChangePosition = value => {
    setUpdatePosition(value);
  };

  const handleAccept = () => {
    updateBranchRequest({
      branch_request_ids: [user.id],
      status: SystemConstant.BRANCH_REQUEST_STATUS.ACCEPTED.status,
      birthday: updateBirthday ? format(updateBirthday, "dd-MM-yyyy") : null,
      unit_id: updateUnit,
      department_id: updateDepartment,
      position_id: updatePosition,
      refetch: refetch,
      onClose: () => onCloseForm,
    });
  };

  // ESC key to close
  const handleKeyDown = useCallback(e => {
    if (e.key === "Escape") {
      if (document.querySelector(".MuiPopover-root, .MuiPopper-root")) return;
      onCloseForm();
    }
  }, [onCloseForm]);

  useEffect(() => {
    if (isOpenFormAccept) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpenFormAccept, handleKeyDown]);

  useEffect(() => {
    if (positionsByUnit) setPositions(positionsByUnit);
  }, [positionsByUnit]);

  useEffect(() => {
    if (positionsByDepartment) setPositions(positionsByDepartment);
  }, [positionsByDepartment]);

  if (!isOpenFormAccept) return null;

  return (
    <Fragment>
      <div className="modal-backdrop fade show" />
      <div className="modal modal-blur fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Thêm thành viên mới</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onCloseForm}></button>
            </div>

            <div className="modal-body" style={{ overflowY: "auto", maxHeight: "70vh", textAlign: "left" }}>
              {user.note && (
                <div className="mb-3">
                  <label className="form-label fw-bold">Ghi chú từ người yêu cầu:</label>
                  <p className="text-muted" style={{ marginBottom: 0 }}>{user.note}</p>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Họ và tên <span className="text-danger">*</span></label>
                <input type="text" className="form-control" defaultValue={user?.name || ""} disabled />
              </div>

              <div className="mb-3">
                <label className="form-label">Số điện thoại <span className="text-danger">*</span></label>
                <input type="text" className="form-control" defaultValue={user?.phone || ""} disabled />
              </div>

              <div className="mb-3">
                <label className="form-label">Email <span className="text-danger">*</span></label>
                <input type="email" className="form-control" defaultValue={user?.email || ""} disabled />
              </div>

              <div className="mb-3">
                <label className="form-label">Ngày sinh</label>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={updateBirthday}
                    format="dd/MM/yyyy"
                    disableFuture
                    onChange={e => setUpdateBirthday(e)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: "DD/MM/YYYY",
                        InputLabelProps: { shrink: true },
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="mb-3">
                <label className="form-label">Đơn vị</label>
                <SearchableSelect
                  value={updateUnit}
                  onChange={handleChangeUnit}
                  placeholder="Chọn đơn vị"
                  options={sortedUnitList.map(u => ({ value: u.unitId, label: u.unitName }))}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Phòng ban</label>
                <SearchableSelect
                  value={updateDepartment}
                  onChange={handleChangeDepartment}
                  placeholder="Chọn phòng ban"
                  disabled={!updateUnit}
                  options={sortedDepartments.map(d => ({ value: d.departmentId, label: d.departmentName }))}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Vị trí</label>
                <SearchableSelect
                  value={updatePosition}
                  onChange={handleChangePosition}
                  placeholder="Chọn vị trí"
                  disabled={!updateUnit}
                  options={sortedPositions.map(p => ({ value: p.positionId, label: p.positionName }))}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline-danger" onClick={onOpenConfirm}>
                Từ chối
              </button>
              <button className="btn btn-primary ms-auto" onClick={handleAccept}>
                Thêm
              </button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default FormUpdateBranchRequest;
